import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper
} from '@mui/material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format, isBefore, addHours } from 'date-fns';
import { useTranslation } from 'react-i18next';

import ServiceSelection from './ServiceSelection';
import DateTimeSelection from './DateTimeSelection';
import BookingSummary from './BookingSummary';

import { useApp } from '../../context/AppContext';
import { Service, TimeSlot } from '../../types';

const ModifyBookingPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { services, appointments, blockedTimes, categories, salonSettings, loading: appLoading, refreshAppointments } = useApp();
  const { t } = useTranslation();

  const steps = [
    t('modify.steps.selectService'),
    t('modify.steps.dateTime'),
    t('modify.steps.confirmation'),
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [originalAppointment, setOriginalAppointment] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const loadAppointment = async () => {
      if (!appointmentId) {
        setError(t('modify.errors.invalidId'));
        setLoading(false);
        return;
      }

      try {
        const appointmentDoc = await getDoc(doc(db, 'appointments', appointmentId));

        if (!appointmentDoc.exists()) {
          setError(t('modify.errors.notFound'));
          setLoading(false);
          return;
        }

        const appointmentData: any = { id: appointmentDoc.id, ...appointmentDoc.data() };

        const appointmentDateTime = new Date(`${appointmentData.date}T${appointmentData.startTime}:00`);
        if (isBefore(appointmentDateTime, new Date())) {
          setError(t('modify.errors.passed'));
          setLoading(false);
          return;
        }

        const twentyFourHoursFromNow = addHours(new Date(), 24);
        if (isBefore(appointmentDateTime, twentyFourHoursFromNow)) {
          setError(t('modify.errors.tooClose'));
          setLoading(false);
          return;
        }

        if (appointmentData.status === 'cancelled') {
          setError(t('modify.errors.alreadyCancelled'));
          setLoading(false);
          return;
        }

        setOriginalAppointment(appointmentData);

        const service = services.find(s => s.id === appointmentData.serviceId);
        if (service) setSelectedService(service);

        setLoading(false);
      } catch (err) {
        setError(t('modify.errors.failedLoad'));
        setLoading(false);
      }
    };

    if (!appLoading.services) {
      loadAppointment();
    }
  }, [appointmentId, appLoading.services, services, t]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    handleNext();
  };

  const handleDateTimeSelect = () => {
    if (selectedDate && selectedTimeSlot) handleNext();
  };

  const handleSubmit = async () => {
    if (!selectedService || !selectedDate || !selectedTimeSlot || !originalAppointment) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const appointmentRef = doc(db, 'appointments', appointmentId!);
      await updateDoc(appointmentRef, {
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: selectedTimeSlot.startTime,
        endTime: selectedTimeSlot.endTime,
        duration: selectedService.duration,
        price: selectedService.price,
        updatedAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      });

      await refreshAppointments();
      setSuccess(true);

      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError(t('modify.errors.failedUpdate'));
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ServiceSelection
            services={services}
            categories={categories}
            selectedService={selectedService}
            onSelectService={handleServiceSelect}
          />
        );
      case 1:
        return selectedService ? (
          <DateTimeSelection
            service={selectedService}
            appointments={appointments.filter(apt => apt.id !== appointmentId)}
            blockedTimes={blockedTimes}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            onSelectDate={setSelectedDate}
            onSelectTimeSlot={setSelectedTimeSlot}
            salonSettings={salonSettings}
          />
        ) : null;
      case 2:
        return success && selectedService && selectedDate && selectedTimeSlot ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('modify.successAlert')}
            </Alert>
            <BookingSummary
              service={selectedService}
              date={selectedDate}
              timeSlot={selectedTimeSlot}
              customerInfo={{
                firstName: originalAppointment.clientName?.split(' ')[0] || '',
                lastName: originalAppointment.clientName?.split(' ').slice(1).join(' ') || '',
                email: originalAppointment.clientEmail || originalAppointment.email || '',
                phone: originalAppointment.clientPhone || originalAppointment.phone || '',
                notes: originalAppointment.notes || '',
                agreeToTerms: true,
                emailNotifications: true,
                smsNotifications: false,
              }}
            />
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t('modify.redirecting')}
              </Typography>
            </Box>
          </>
        ) : (
          selectedService && selectedDate && selectedTimeSlot && originalAppointment && (
            <>
              <Typography variant="h5" gutterBottom>
                {t('modify.reviewTitle')}
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('modify.reviewAlert')}
              </Alert>

              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('modify.originalLabel')}
                </Typography>
                <Typography variant="body2">
                  {originalAppointment.serviceName} - {format(new Date(originalAppointment.date), 'EEEE, MMMM d, yyyy')} at {originalAppointment.startTime}
                </Typography>
              </Paper>

              <BookingSummary
                service={selectedService}
                date={selectedDate}
                timeSlot={selectedTimeSlot}
                customerInfo={{
                  firstName: originalAppointment.clientName?.split(' ')[0] || '',
                  lastName: originalAppointment.clientName?.split(' ').slice(1).join(' ') || '',
                  email: originalAppointment.clientEmail || originalAppointment.email || '',
                  phone: originalAppointment.clientPhone || originalAppointment.phone || '',
                  notes: originalAppointment.notes || '',
                  agreeToTerms: true,
                  emailNotifications: true,
                  smsNotifications: false,
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button onClick={handleBack}>{t('modify.back')}</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  sx={{ color: 'text.primary' }}
                >
                  {isSubmitting ? t('modify.updating') : t('modify.confirmChanges')}
                </Button>
              </Box>
            </>
          )
        );
      default:
        return null;
    }
  };

  if (loading || appLoading.services) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('modify.loading')}</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          {t('modify.returnHome')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" align="center" gutterBottom>
        {t('modify.title')}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: 4 }}>
        {renderStepContent(activeStep)}
      </Box>

      {activeStep === 1 && !success && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleBack}>{t('modify.back')}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDateTimeSelect}
            disabled={!selectedDate || !selectedTimeSlot}
            sx={{ color: 'text.primary' }}
          >
            {t('modify.next')}
          </Button>
        </Box>
      )}

      {isSubmitting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <Paper
            elevation={3}
            sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
          >
            <CircularProgress />
            <Typography>{t('modify.updatingOverlay')}</Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default ModifyBookingPage;
